import { Link, NavLink, useLocation } from 'react-router-dom';
import { Fragment, useEffect, useState } from 'react';
import { Dialog, Popover, Transition } from '@headlessui/react';
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import Logo from './brand/Logo';
import Icon, { type IconName } from './Icon';
import { categories } from '../data/categories';
import { company } from '../data/company';

const navigation = [
  { name: 'Products', to: '/products', dropdown: true },
  { name: 'Industries', to: '/industries' },
  { name: 'Product Finder', to: '/product-finder' },
  { name: 'About', to: '/about' },
  { name: 'Contact', to: '/contact' },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `text-xs font-semibold uppercase tracking-[0.12em] transition-colors ${
      isActive ? 'text-primary' : 'text-text-primary hover:text-primary'
    }`;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-surface/90 shadow-sm backdrop-blur-md' : 'bg-surface/60 backdrop-blur-sm'
      }`}
    >
      <nav className="container-page flex items-center justify-between py-3.5" aria-label="Global">
        <Logo />

        {/* Desktop nav */}
        <div className="hidden items-center gap-8 lg:flex">
          {navigation.map((item) =>
            item.dropdown ? (
              <Popover key={item.name} className="relative">
                {({ open }) => (
                  <>
                    <Popover.Button className="flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.12em] text-text-primary outline-none hover:text-primary">
                      {item.name}
                      <ChevronDownIcon
                        className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
                        aria-hidden="true"
                      />
                    </Popover.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="opacity-0 translate-y-1"
                      enterTo="opacity-100 translate-y-0"
                      leave="transition ease-in duration-150"
                      leaveFrom="opacity-100 translate-y-0"
                      leaveTo="opacity-0 translate-y-1"
                    >
                      <Popover.Panel className="absolute left-1/2 z-10 mt-4 w-80 -translate-x-1/2">
                        {({ close }) => (
                          <div className="overflow-hidden rounded-2xl bg-surface shadow-card ring-1 ring-ink/5">
                            <Link
                              to="/products"
                              onClick={() => close()}
                              className="block bg-primary px-5 py-3 text-sm font-semibold text-white hover:bg-primary-dark"
                            >
                              All Products
                            </Link>
                            <div className="p-2">
                              {categories.map((cat) => (
                                <Link
                                  key={cat.slug}
                                  to={`/products/${cat.slug}`}
                                  onClick={() => close()}
                                  className="flex items-start gap-3 rounded-xl p-3 hover:bg-muted"
                                >
                                  <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-primary/8 text-primary">
                                    <Icon name={cat.icon as IconName} className="h-5 w-5" />
                                  </span>
                                  <span>
                                    <span className="block text-sm font-semibold text-text-primary">
                                      {cat.name}
                                    </span>
                                    <span className="mt-0.5 block text-xs leading-5 text-text-secondary">
                                      {cat.tagline}
                                    </span>
                                  </span>
                                </Link>
                              ))}
                            </div>
                          </div>
                        )}
                      </Popover.Panel>
                    </Transition>
                  </>
                )}
              </Popover>
            ) : (
              <NavLink key={item.name} to={item.to} className={linkClass}>
                {item.name}
              </NavLink>
            ),
          )}
        </div>

        <div className="hidden lg:flex">
          <Link to="/contact" className="btn-primary px-5 py-2.5">
            Get a Quote
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-md p-2 text-text-primary lg:hidden"
          onClick={() => setMobileMenuOpen(true)}
        >
          <span className="sr-only">Open menu</span>
          <Bars3Icon className="h-6 w-6" aria-hidden="true" />
        </button>
      </nav>

      {/* Mobile menu */}
      <Dialog as="div" className="lg:hidden" open={mobileMenuOpen} onClose={setMobileMenuOpen}>
        <div className="fixed inset-0 z-50 bg-ink/30 backdrop-blur-sm" aria-hidden="true" />
        <Dialog.Panel className="fixed inset-y-0 right-0 z-50 w-full max-w-sm overflow-y-auto bg-surface px-6 py-5 shadow-xl">
          <div className="flex items-center justify-between">
            <Logo />
            <button
              type="button"
              className="rounded-md p-2 text-text-primary"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-8 flow-root">
            <div className="divide-y divide-ink/5">
              <div className="space-y-1 pb-6">
                <p className="px-3 pb-2 text-xs font-semibold uppercase tracking-wider text-secondary-dark">
                  Products
                </p>
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/products/${cat.slug}`}
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-base font-medium text-text-primary hover:bg-muted"
                  >
                    <Icon name={cat.icon as IconName} className="h-5 w-5 text-primary" />
                    {cat.name}
                  </Link>
                ))}
              </div>
              <div className="space-y-1 py-6">
                {navigation
                  .filter((n) => !n.dropdown)
                  .map((item) => (
                    <Link
                      key={item.name}
                      to={item.to}
                      className="block rounded-lg px-3 py-2.5 text-base font-semibold text-text-primary hover:bg-muted"
                    >
                      {item.name}
                    </Link>
                  ))}
              </div>
              <div className="py-6">
                <Link to="/contact" className="btn-primary w-full">
                  Get a Quote
                </Link>
                <a
                  href={`mailto:${company.email}`}
                  className="mt-3 block text-center text-sm text-text-secondary"
                >
                  {company.email}
                </a>
              </div>
            </div>
          </div>
        </Dialog.Panel>
      </Dialog>
    </header>
  );
}
